import { useRef, useState, type ReactNode } from "react";
import { IconArrowDown, IconArrowUp, IconTable, IconX } from "@tabler/icons-react";
import { Badge, Card, EmptyState, Loader, Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import { extractApiMessage } from "@/shared/api/extract-message";
import type { PreviewInfinito } from "../hooks/use-preview";
import type { RepDefinicion } from "../services/reporteria.api";
import * as ops from "../state/builder-ops";

function celda(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  return String(v);
}

/**
 * Tabla de vista previa con scroll infinito. Recibe el resultado del hook de
 * preview (usuario o admin en contexto de config), así el mismo componente sirve
 * al builder de usuario y al de predefinidos. Si se pasan <c>def</c>+<c>setDef</c>,
 * los encabezados son INTERACTIVOS (ordenar al clic, renombrar al doble clic,
 * quitar) como en el reporteador legado.
 */
export function PreviewTabla({
  activo,
  datos,
  acciones,
  def,
  setDef,
}: {
  activo: boolean;
  datos: PreviewInfinito;
  acciones?: ReactNode;
  def?: RepDefinicion;
  setDef?: (f: (d: RepDefinicion) => RepDefinicion) => void;
}) {
  const { preview, total, columnas, filas } = datos;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [renombrando, setRenombrando] = useState<number | null>(null);
  const interactivo = !!def && !!setDef;

  // Mapea la columna del preview (por índice) a la columna de la definición: las
  // primeras son las columnas base (def.columnas); las siguientes, las columnas
  // calculadas de tipo fila (las agregadas van al pie, no como columna).
  function refColumna(i: number) {
    if (!def) return null;
    const base = def.columnas.length;
    if (i < base) return { tipo: "base" as const, tablaAlias: def.columnas[i].tablaAlias, token: def.columnas[i].token };
    const filaCalc = (def.columnasCalculadas ?? [])
      .map((c, idx) => ({ c, idx }))
      .filter((x) => (x.c.tipo ?? "fila") !== "agregado");
    const item = filaCalc[i - base];
    return item ? { tipo: "calc" as const, calcIndex: item.idx } : null;
  }

  function ciclarOrden(i: number) {
    const r = refColumna(i);
    if (r?.tipo === "base") setDef!((d) => ops.ciclarOrden(d, r.tablaAlias, r.token));
  }
  function quitar(i: number) {
    const r = refColumna(i);
    if (r?.tipo === "base") setDef!((d) => ops.removeColumna(d, i));
    else if (r?.tipo === "calc") setDef!((d) => ops.removeCalc(d, r.calcIndex));
  }
  function renombrar(i: number, nuevo: string) {
    const r = refColumna(i);
    if (r?.tipo === "base") setDef!((d) => ops.updateColumna(d, i, { etiqueta: nuevo }));
    else if (r?.tipo === "calc") setDef!((d) => ops.updateCalc(d, r.calcIndex, { nombre: nuevo }));
    setRenombrando(null);
  }
  function dirOrden(i: number): "asc" | "desc" | null {
    const r = refColumna(i);
    return r?.tipo === "base" && def ? ops.ordenDe(def, r.tablaAlias, r.token) : null;
  }

  function onScroll() {
    const el = scrollRef.current;
    if (!el || !preview.hasNextPage || preview.isFetchingNextPage) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight * 0.75) preview.fetchNextPage();
  }

  return (
    <Card className="flex h-full flex-col p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Text fw={600}>Vista previa</Text>
          {activo && (
            <Badge variant="light" size="xs">
              {total.isLoading ? "contando…" : total.data != null ? `${total.data.toLocaleString("es-MX")} filas` : "—"}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">{acciones}</div>
      </div>

      {!activo ? (
        <EmptyState
          icon={IconTable}
          title="Arma tu reporte"
          description="Elige al menos una columna para ver la vista previa."
        />
      ) : preview.isError ? (
        <Text size="sm" c="red" className="p-2">
          {extractApiMessage(preview.error)}
        </Text>
      ) : (
        <div ref={scrollRef} onScroll={onScroll} className="min-h-0 flex-1 overflow-auto rounded-md border border-border">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr>
                {columnas.map((c, i) => {
                  const dir = interactivo ? dirOrden(i) : null;
                  return (
                    <th key={c.clave} className="group whitespace-nowrap border-b border-border px-3 py-2 text-left font-semibold">
                      {renombrando === i ? (
                        <input
                          autoFocus
                          defaultValue={c.etiqueta}
                          className="h-6 w-full min-w-24 rounded border border-border bg-background px-1 text-sm font-normal"
                          onBlur={(e) => renombrar(i, e.currentTarget.value.trim() || c.etiqueta)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") renombrar(i, e.currentTarget.value.trim() || c.etiqueta);
                            if (e.key === "Escape") setRenombrando(null);
                          }}
                        />
                      ) : (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            disabled={!interactivo}
                            onClick={() => ciclarOrden(i)}
                            onDoubleClick={() => interactivo && setRenombrando(i)}
                            title={interactivo ? "Clic: ordenar · doble clic: renombrar" : undefined}
                            className={cn("flex items-center gap-1 truncate", interactivo && "cursor-pointer hover:text-multiva-700")}
                          >
                            <span className="truncate">{c.etiqueta}</span>
                            {dir === "asc" && <IconArrowUp size={13} className="shrink-0 text-multiva-600" />}
                            {dir === "desc" && <IconArrowDown size={13} className="shrink-0 text-multiva-600" />}
                          </button>
                          {interactivo && (
                            <button
                              type="button"
                              aria-label="Quitar columna"
                              onClick={() => quitar(i)}
                              className="ml-auto shrink-0 rounded p-0.5 text-muted-foreground opacity-0 hover:bg-muted-foreground/10 hover:text-foreground group-hover:opacity-100"
                            >
                              <IconX size={13} />
                            </button>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, i) => (
                <tr key={i} className="odd:bg-background even:bg-muted/40">
                  {columnas.map((c) => (
                    <td key={c.clave} className="whitespace-nowrap border-b border-border/60 px-3 py-1.5">
                      {celda(fila[c.clave])}
                    </td>
                  ))}
                </tr>
              ))}
              {preview.isLoading && (
                <tr>
                  <td colSpan={Math.max(1, columnas.length)} className="px-3 py-6 text-center">
                    <Loader size="sm" />
                  </td>
                </tr>
              )}
              {!preview.isLoading && filas.length === 0 && (
                <tr>
                  <td colSpan={Math.max(1, columnas.length)} className="px-3 py-6 text-center text-muted-foreground">
                    Sin resultados para esta definición.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {preview.isFetchingNextPage && (
            <div className="flex justify-center py-2">
              <Loader size="xs" />
            </div>
          )}
        </div>
      )}

      {/* Pie de totales · columnas calculadas de tipo agregado (RN-18) */}
      {activo && datos.totales && Object.keys(datos.totales).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 border-t border-border pt-2 text-sm">
          {Object.entries(datos.totales).map(([k, v]) => (
            <span key={k}>
              <span className="text-muted-foreground">{k}:</span>{" "}
              <span className="font-semibold tabular-nums">{celda(v)}</span>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}
